/**
 * Overlay a staged (not-yet-saved) card order on top of the canonical list the
 * BE returned.
 *
 * Extracted from PublishedWorkflows so the merge rules are testable without
 * driving dnd-kit through jsdom. The drag itself is just `arrayMove`; the part
 * worth pinning is what happens when the staged order and the live list have
 * drifted apart — the Approver can sit in Rearrange mode while another
 * approver publishes or unpublishes a workflow underneath them.
 *
 * Rules:
 *   - `null` draft (nothing dragged yet) → the canonical order, untouched.
 *   - Ids in the draft that are no longer in `items` are dropped rather than
 *     leaving a hole (a workflow unpublished since the draft was taken).
 *   - Items missing from the draft are appended in their canonical order (a
 *     workflow published since the draft was taken). Appending rather than
 *     discarding matters: the save path renumbers 0..N-1 over whatever this
 *     returns, so a dropped card would silently keep its stale Order.
 */
export function applyDraftOrder<T extends { id: string }>(
  items: readonly T[],
  draftOrder: readonly string[] | null,
): T[] {
  if (!draftOrder) return [...items];
  const drafted = new Set(draftOrder);
  return [
    ...draftOrder
      .map((id) => items.find((item) => item.id === id))
      .filter((item): item is T => Boolean(item)),
    ...items.filter((item) => !drafted.has(item.id)),
  ];
}
